import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { authSuccessSchema, type SessionUser } from '@/features/auth/auth-schemas';
import { api } from '@/lib/api/api-client';
import { ApiError } from '@/lib/api/api-error';
import { useSessionStore } from '@/stores/session-store';

export const authKeys = {
  session: ['auth', 'session'] as const,
};

/**
 * Boot-time silent restore. The access token is memory-only, so every page load
 * starts unauthenticated - the 30-day refresh cookie is what makes the session
 * outlive a reload. Resolves to null when there is no valid cookie, rather than
 * throwing, since "signed out" is a normal outcome here.
 */
export function useSessionRestore(): UseQueryResult<SessionUser | null> {
  const setAccessToken = useSessionStore((state) => state.setAccessToken);

  return useQuery({
    queryKey: authKeys.session,
    queryFn: async () => {
      try {
        const response = await api.post<unknown>('/auth/refresh', undefined, { anonymous: true });
        const session = authSuccessSchema.parse(response);
        setAccessToken(session.accessToken, Date.now() + session.expiresInSeconds * 1000);
        return session.user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}

export function useCurrentUser(): SessionUser | null {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<SessionUser | null>(authKeys.session) ?? null;
}
